import clsx from 'clsx';
import { CSSProperties, FormEvent, ReactNode } from 'react';

import styles from './Form.module.css';
import { FormFieldContext } from './FormFieldContext';

interface FormProps {
  id?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
}

const Form = ({ id, className, style, children, onSubmit }: FormProps) => (
  <form
    id={id}
    className={clsx(styles.form, className)}
    style={style}
    onSubmit={(e) => {
      // Matches the previous (Semantic UI) behaviour, which never let the browser navigate on submit
      e.preventDefault();
      onSubmit?.(e);
    }}
  >
    {children}
  </form>
);

interface FormFieldProps {
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  /** Highlights the field's label and inputs in red */
  error?: boolean;
  /** Adds an asterisk after the field's label */
  required?: boolean;
}

const FormField = ({
  className,
  style,
  children,
  error,
  required,
}: FormFieldProps) => (
  <div
    className={clsx(
      styles.field,
      error && styles.error,
      required && styles.required,
      className,
    )}
    style={style}
  >
    <FormFieldContext.Provider value={{ error: !!error }}>
      {children}
    </FormFieldContext.Provider>
  </div>
);

interface FormGroupProps {
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  /** Gives every field in the group an equal width */
  widths?: 2 | 3;
  /** Keep the fields side by side on mobile, rather than stacking them */
  unstackable?: boolean;
}

const FormGroup = ({
  className,
  style,
  children,
  widths,
  unstackable,
}: FormGroupProps) => (
  <div
    className={clsx(
      styles.fields,
      widths === 2 && styles.twoFields,
      widths === 3 && styles.threeFields,
      unstackable && styles.unstackable,
      className,
    )}
    style={style}
  >
    {children}
  </div>
);

Form.Field = FormField;
Form.Group = FormGroup;

export default Form;
